import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
  Typography,
  Tag,
  Alert,
  Divider,
  message,
  Spin
} from 'antd';
import {
  GlobalOutlined,
  EuroCircleOutlined,
  HomeOutlined,
  TruckOutlined,
  ContainerOutlined,
  EditOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

import { unifiedShipmentsAPI, importerAPI, supplierAPI, containerTypeAPI } from '../services/api.ts';
import { UnifiedShipment, UnifiedShipmentType } from '../types.ts';

const { Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface Supplier {
  id: number;
  name: string;
  origin_type_id?: number;
  origin_type?: {
    id: number;
    code: string;
    description: string;
  };
}

interface Importer {
  id: number;
  ragione_sociale: string;
}

interface Warehouse {
  id: number;
  description: string;
  cod_mag?: string;
  is_nationalized: boolean;
}

interface TransportType {
  id: number;
  code: string;
  description: string;
}

interface ShipmentStatus {
  id: number;
  code: string;
  description: string;
}

interface ContainerType {
  id: number;
  code: string;
  description: string;
}

interface ShipmentEditModalProps {
  visible: boolean;
  shipment: UnifiedShipment | null;
  onClose: () => void;
  onSuccess: () => void;
}

const ShipmentEditModal: React.FC<ShipmentEditModalProps> = ({
  visible,
  shipment,
  onClose,
  onSuccess
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Dati di lookup
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [importers, setImporters] = useState<Importer[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [transportTypes, setTransportTypes] = useState<TransportType[]>([]);
  const [containerTypes, setContainerTypes] = useState<ContainerType[]>([]);
  const [statuses, setStatuses] = useState<ShipmentStatus[]>([]);

  // Stato derivato
  const [selectedTransportTypeCode, setSelectedTransportTypeCode] = useState<string | null>(null);

  // Carica dati di lookup
  useEffect(() => {
    if (visible && shipment) {
      loadLookupData();
    }
  }, [visible, shipment]);

  const loadLookupData = async () => {
    if (!shipment) return;

    setLoading(true);
    try {
      const [suppliersRes, importersRes, transportTypesRes, containerTypesRes, statusesRes, warehousesRes] = await Promise.all([
        supplierAPI.getSuppliers(),
        importerAPI.getImporters(),
        unifiedShipmentsAPI.getTransportTypes(),
        containerTypeAPI.getContainerTypes({ is_active: true }),
        unifiedShipmentsAPI.getStatuses(),
        unifiedShipmentsAPI.getWarehouses(shipment.shipment_type)
      ]);

      setSuppliers(suppliersRes?.suppliers || suppliersRes || []);
      setImporters(importersRes || []);
      setTransportTypes(transportTypesRes || []);
      setContainerTypes(containerTypesRes || []);
      setStatuses(statusesRes || []);
      setWarehouses(warehousesRes || []);

      // Imposta il tipo di trasporto corrente
      setSelectedTransportTypeCode(shipment.transport_type_code || null);

      // Popola il form con i dati esistenti
      form.setFieldsValue({
        shipment_code: shipment.shipment_code,
        description: shipment.description,
        supplier_id: shipment.supplier_id,
        importer_id: shipment.importer_id,
        warehouse_id: shipment.warehouse_id,
        transport_type_id: shipment.transport_type_id,
        container_type_id: shipment.container_type_id,
        transport_document_type: shipment.transport_document_type,
        transport_document_number: shipment.transport_document_number,
        carrier_name: shipment.carrier_name,
        vehicle_plate: shipment.vehicle_plate,
        departure_date: shipment.departure_date ? dayjs(shipment.departure_date) : null,
        expected_arrival_date: shipment.expected_arrival_date ? dayjs(shipment.expected_arrival_date) : null,
        status_id: shipment.status_id,
        notes: shipment.notes
      });
    } catch (error: any) {
      console.error('Error loading lookup data:', error);
      message.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const getShipmentTypeIcon = (type: UnifiedShipmentType | null) => {
    switch (type) {
      case 'EXTRA_UE':
        return <GlobalOutlined style={{ color: '#1890ff' }} />;
      case 'UE':
        return <EuroCircleOutlined style={{ color: '#52c41a' }} />;
      case 'NAZIONALE':
        return <HomeOutlined style={{ color: '#fa8c16' }} />;
      default:
        return <TruckOutlined />;
    }
  };

  const getShipmentTypeColor = (type: UnifiedShipmentType | null) => {
    switch (type) {
      case 'EXTRA_UE':
        return 'blue';
      case 'UE':
        return 'green';
      case 'NAZIONALE':
        return 'orange';
      default:
        return 'default';
    }
  };

  const getShipmentTypeLabel = (type: UnifiedShipmentType | null) => {
    switch (type) {
      case 'EXTRA_UE':
        return 'Extra-UE (Container)';
      case 'UE':
        return 'Unione Europea';
      case 'NAZIONALE':
        return 'Nazionale';
      default:
        return 'Seleziona fornitore';
    }
  };

  const handleSubmit = async () => {
    if (!shipment) return;

    try {
      const values = await form.validateFields();
      setSubmitting(true);

      const data = {
        ...values,
        departure_date: values.departure_date?.format('YYYY-MM-DD'),
        expected_arrival_date: values.expected_arrival_date?.format('YYYY-MM-DD')
      };

      await unifiedShipmentsAPI.updateUnifiedShipment(shipment.id, data);
      message.success('Spedizione aggiornata con successo');
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error updating shipment:', error);
      message.error(error.response?.data?.detail || 'Errore durante l\'aggiornamento della spedizione');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    setSelectedTransportTypeCode(null);
    onClose();
  };

  if (!shipment) return null;

  return (
    <Modal
      title={
        <span>
          <EditOutlined style={{ marginRight: 8 }} />
          Modifica Spedizione - {shipment.shipment_code}
        </span>
      }
      open={visible}
      onOk={handleSubmit}
      onCancel={handleClose}
      width={800}
      okText="Salva Modifiche"
      cancelText="Annulla"
      confirmLoading={submitting}
      destroyOnClose
    >
      <Spin spinning={loading}>
        {/* Tipo spedizione (read-only) */}
        <Alert
          message={
            <span>
              {getShipmentTypeIcon(shipment.shipment_type)}
              <span style={{ marginLeft: 8 }}>
                Tipo Spedizione: <Tag color={getShipmentTypeColor(shipment.shipment_type)}>{getShipmentTypeLabel(shipment.shipment_type)}</Tag>
              </span>
              <Text type="secondary" style={{ marginLeft: 16 }}>
                Fornitore: {shipment.supplier_name}
              </Text>
            </span>
          }
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Form
          form={form}
          layout="vertical"
        >
          <Divider orientation="left">Informazioni Base</Divider>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="shipment_code"
                label="Codice Spedizione"
                rules={[{ required: true, message: 'Inserisci codice spedizione' }]}
              >
                <Input placeholder="es. SHIP-2024-001" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="status_id"
                label="Stato"
                rules={[{ required: true, message: 'Seleziona stato' }]}
              >
                <Select placeholder="Seleziona stato">
                  {/* Sequenza obbligatoria (specchio del backend): stato attuale + solo il
                      successivo; "Evaso" è automatico dal carico merce / IM7-IM4 */}
                  {statuses
                    .filter(status => {
                      const current = statuses.find(s => s.id === shipment?.status_id);
                      if (!current) return status.code !== 'completed';
                      const next: Record<string, string[]> = {
                        in_preparation: ['shipped'],
                        shipped: ['in_transit'],
                        in_transit: ['arrived'],
                      };
                      return status.id === current.id || (next[current.code] || []).includes(status.code);
                    })
                    .map(status => (
                      <Option key={status.id} value={status.id}>
                        {status.description}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="description"
                label="Descrizione"
              >
                <Input placeholder="Descrizione opzionale della spedizione" />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Trasporto</Divider>

          <Row gutter={16}>
            <Col span={selectedTransportTypeCode === 'CONTAINER' ? 6 : 8}>
              <Form.Item
                name="transport_type_id"
                label="Tipo Trasporto"
                rules={[{ required: true, message: 'Seleziona tipo trasporto' }]}
              >
                <Select
                  placeholder="Seleziona tipo"
                  onChange={(value) => {
                    const transportType = transportTypes.find(t => t.id === value);
                    setSelectedTransportTypeCode(transportType?.code || null);
                    // Reset container_type_id se il tipo trasporto non è CONTAINER
                    if (transportType?.code !== 'CONTAINER') {
                      form.setFieldsValue({ container_type_id: undefined });
                    }
                  }}
                >
                  {transportTypes.map(type => (
                    <Option key={type.id} value={type.id}>
                      {type.code === 'CONTAINER' && <ContainerOutlined style={{ marginRight: 4 }} />}
                      {type.code === 'TRUCK' && <TruckOutlined style={{ marginRight: 4 }} />}
                      {type.description}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            {selectedTransportTypeCode === 'CONTAINER' && (
              <Col span={6}>
                <Form.Item
                  name="container_type_id"
                  label="Tipo Container"
                >
                  <Select placeholder="Seleziona tipo container" allowClear>
                    {containerTypes.map(ct => (
                      <Option key={ct.id} value={ct.id}>
                        {ct.description}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            )}
            <Col span={selectedTransportTypeCode === 'CONTAINER' ? 6 : 8}>
              <Form.Item
                name="transport_document_type"
                label="Tipo Documento Trasporto"
              >
                <Select placeholder="Seleziona tipo" allowClear>
                  <Option value="CMR">CMR</Option>
                  <Option value="BL">Bill of Lading (BL)</Option>
                  <Option value="AWB">Air Waybill (AWB)</Option>
                  <Option value="DDT">DDT</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={selectedTransportTypeCode === 'CONTAINER' ? 6 : 8}>
              <Form.Item
                name="transport_document_number"
                label="Numero Documento"
              >
                <Input placeholder="Numero documento trasporto" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="carrier_name"
                label="Vettore/Spedizioniere"
              >
                <Input placeholder="Nome vettore o spedizioniere" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="vehicle_plate"
                label="Targa/Numero Container"
              >
                <Input placeholder="Targa veicolo o numero container" />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Date e Destinazione</Divider>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="departure_date"
                label="Data Partenza"
              >
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="expected_arrival_date"
                label="Data Arrivo Prevista"
              >
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="warehouse_id"
                label="Magazzino Destinazione"
                rules={[{ required: true, message: 'Seleziona magazzino' }]}
              >
                <Select placeholder="Seleziona magazzino">
                  {warehouses.map(wh => (
                    <Option key={wh.id} value={wh.id}>
                      {wh.cod_mag && `[${wh.cod_mag}] `}{wh.description}
                      <Tag
                        color={wh.is_nationalized ? 'green' : 'blue'}
                        style={{ marginLeft: 8 }}
                      >
                        {wh.is_nationalized ? 'Nazionale' : 'Doganale'}
                      </Tag>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="importer_id"
                label="Importatore"
              >
                <Select
                  placeholder="Seleziona importatore"
                  allowClear
                  showSearch
                  optionFilterProp="children"
                >
                  {importers.map(imp => (
                    <Option key={imp.id} value={imp.id}>
                      {imp.ragione_sociale}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Note</Divider>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="notes"
                label="Note"
              >
                <TextArea rows={3} placeholder="Note aggiuntive sulla spedizione" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Spin>
    </Modal>
  );
};

export default ShipmentEditModal;
